import { Controller } from 'stimulus';

// Infinite scroll via IntersectionObserver. The list container hosts the
// rendered cards plus a single sentinel element at the bottom carrying
// `data-next-url`. When the sentinel scrolls into view, we fetch that URL,
// append the response HTML to the container (which includes the next
// sentinel — or doesn't, when we've reached the last page), and re-observe
// the new sentinel.
//
// Generic by design: any list that follows the (container + sentinel with
// data-next-url) convention can use this controller.
export default class extends Controller {
  connect() {
    this.loading = false;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.loading) this.loadMore();
      });
    }, { rootMargin: '200px 0px' });
    this.observeSentinel();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  observeSentinel() {
    const sentinel = this.element.querySelector('[data-infinite-scroll-sentinel]');
    if (sentinel) this.observer.observe(sentinel);
  }

  loadMore() {
    const sentinel = this.element.querySelector('[data-infinite-scroll-sentinel]');
    if (!sentinel) return;
    const url = sentinel.dataset.nextUrl;
    if (!url) return;

    this.loading = true;
    this.observer.unobserve(sentinel);

    fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        sentinel.remove();
        this.element.insertAdjacentHTML('beforeend', html);
        this.observeSentinel();
      })
      .catch(() => {
        // Restore the sentinel so the user can retry by scrolling away and back.
        this.observer.observe(sentinel);
      })
      .finally(() => {
        this.loading = false;
      });
  }
}
